import * as React from "react";
import {
    GetDetectedClientBuildId,
    IsClientUpdateNoticeDevelopmentOverrideEnabled,
    SubscribeToClientUpdate,
    SubscribeToClientUpdateNoticeDevelopmentOverride,
} from "source/app/services/ClientUpdate/ClientUpdateChecker";
import { IsProduction } from "source/app/services/External/Environment/Environment";
import * as styles from "./ClientUpdateNotice.css";

const automaticReloadDelaySeconds = 60;
const autoReloadAttemptStorageKey = "motw.clientUpdateReloadAttempted";

export default function ClientUpdateNotice() {
    const [updateAvailable, setUpdateAvailable] = React.useState(false);
    const [secondsUntilReload, setSecondsUntilReload] = React.useState(
        automaticReloadDelaySeconds
    );
    const [developmentOverrideEnabled, setDevelopmentOverrideEnabled] =
        React.useState(IsClientUpdateNoticeDevelopmentOverrideEnabled);

    React.useEffect(() => {
        const unsubscribe = SubscribeToClientUpdate(() =>
            setUpdateAvailable(true)
        );
        return () => {
            unsubscribe();
        };
    }, []);

    React.useEffect(
        () =>
            SubscribeToClientUpdateNoticeDevelopmentOverride(
                setDevelopmentOverrideEnabled
            ),
        []
    );

    const shouldShowNotice =
        updateAvailable || (!IsProduction() && developmentOverrideEnabled);
    const detectedBuildId = GetDetectedClientBuildId() || "unknown";
    const reloadAlreadyAttempted =
        shouldShowNotice &&
        GetAutoReloadAttemptedBuildId() === detectedBuildId;

    React.useEffect(() => {
        if (!shouldShowNotice) {
            return;
        }

        if (reloadAlreadyAttempted) {
            return;
        }

        const startedAt = Date.now();
        setSecondsUntilReload(automaticReloadDelaySeconds);

        const intervalId = window.setInterval(() => {
            const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);
            const remainingSeconds = Math.max(
                0,
                automaticReloadDelaySeconds - elapsedSeconds
            );

            setSecondsUntilReload(remainingSeconds);

            if (remainingSeconds === 0) {
                window.clearInterval(intervalId);
                ReloadForClientUpdate(detectedBuildId);
            }
        }, 1000);

        return () => window.clearInterval(intervalId);
    }, [detectedBuildId, reloadAlreadyAttempted, shouldShowNotice]);

    if (!shouldShowNotice) {
        return null;
    }

    return (
        <div className={styles.container} role="alert" aria-live="polite">
            <span>
                {reloadAlreadyAttempted
                    ? "The update is still available. Refresh to try again."
                    : "A new version is available."}
                {!reloadAlreadyAttempted && (
                    <>
                        <br />
                        Refreshing in {secondsUntilReload} seconds...
                    </>
                )}
            </span>
            <button
                type="button"
                onClick={() => ReloadForClientUpdate(detectedBuildId)}
            >
                Refresh
            </button>
        </div>
    );
}

function GetAutoReloadAttemptedBuildId() {
    try {
        return sessionStorage.getItem(autoReloadAttemptStorageKey);
    } catch (_error) {
        return null;
    }
}

function ReloadForClientUpdate(buildId: string) {
    try {
        sessionStorage.setItem(autoReloadAttemptStorageKey, buildId);
    } catch (_error) {
        // A storage restriction must not prevent a user-requested refresh.
    }

    window.location.reload();
}
